import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import Alert from '../../components/Alert/Alert';
import api from '../../api';
import { useAuth } from '../../contexts/AuthProvider';
import * as XLSX from 'xlsx';
import { formatDateDDMMYYYY } from '../../utils/formatters';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import { getActivePeriodRange, getActiveFilterCount, getPeriodDateRange } from '../../components/common/AdvancedFilter/periodUtils';

const DEFAULT_PAGE_SIZE = 10;

// Map frontend column keys to backend sort property names (Spring sort param) — backend-based
const PROJECTS_REPORT_SORT_KEY_TO_BACKEND = {
  project: 'projectCode',
  client: 'companyName',
  projectDate: 'projectDate',
  validUntil: 'validUntil',
  partnerType: 'partnerType',
  amount: 'grandTotal',
  createdAt: 'createdAt',
  assignedTo: 'assignedTo',
  createdBy: 'createdBy',
  department: 'departmentName',
  projectName: 'projectName',
};

// Serialize filter value (array or string) to API param: "all" or "id1,id2,..."
const toFilterParam = (v) => {
  if (Array.isArray(v)) {
    const clean = v.filter((x) => x != null && x !== '' && x !== 'all');
    return clean.length === 0 ? 'all' : clean.join(',');
  }
  return v == null || v === '' ? 'all' : String(v);
};

/** Convert the original project total to IDR using its creation-time rate. */
const getProjectAmountInIdr = (item) => {
  const sourceAmount = Number(item.grandTotalDisplayCurrency ?? item.grandTotal ?? 0);
  if (!Number.isFinite(sourceAmount)) return 0;

  const currencyCode = String(item.displayCurrencyCode || 'IDR').trim().toUpperCase();
  if (currencyCode === 'IDR') return sourceAmount;

  const creationRate = Number(item.exchangeRateAtCreation);
  return Number.isFinite(creationRate) && creationRate > 0
    ? sourceAmount * creationRate
    : sourceAmount;
};

const getEmptyProjectsFilters = () => ({
  period: '',
  startDate: '',
  endDate: '',
  assignedUser: [],
  createdBy: [],
  department: [],
  branchId: [],
  sector: [],
  standards: [],
  source: [],
  service: [],
  taggingIds: [],
  createdAtFrom: '',
  createdAtTo: '',
  projectStartDateFrom: '',
  projectStartDateTo: '',
  projectEndDateFrom: '',
  projectEndDateTo: '',
  proposeDateFrom: '',
  proposeDateTo: '',
});

const ProjectsReports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loadPersistedState, persistState } = usePersistedFilters('projects_report');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [projectsData, setProjectsData] = useState([]);

  // Server-side pagination (backend returns Page<T>)
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // Server-side sort (ascending/descending handled by backend)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Use global auth context with capabilities
  const { user, can, capabilitiesLoading } = useAuth();

  // Helper function to check project-specific capabilities
  const canProject = (capabilityKey) => can(capabilityKey, 'project');

  // filterData: in-progress (live edits inside offcanvas)
  // appliedFilters: committed (what the API actually uses)
  const [filterData, setFilterData] = useState(getEmptyProjectsFilters);
  const [appliedFilters, setAppliedFilters] = useState(getEmptyProjectsFilters);

  // State to track if filters were initialized from URL
  const [filtersInitializedFromUrl, setFiltersInitializedFromUrl] = useState(false);
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

  // Determine which filters to show based on permissions
  const getEnabledFilters = () => {
    if (canProject('view_global')) {
      return ['assignedUser', 'createdBy', 'department', 'branchId', 'sector', 'standards', 'source', 'service', 'taggingIds'];
    } else if (canProject('view_department')) {
      return ['assignedUser', 'createdBy', 'department', 'branchId', 'sector', 'standards', 'source', 'service', 'taggingIds'];
    } else if (canProject('view_own')) {
      return ['sector', 'standards', 'source', 'service', 'taggingIds'];
    }
    return ['sector', 'standards', 'source', 'service', 'taggingIds'];
  };

  // Get filter overrides for view_department users
  const getFilterOverrides = () => {
    if (canProject('view_department') && !canProject('view_global')) {
      return {
        department: user?.departmentId ? [String(user.departmentId)] : [],
        isDepartmentLocked: true,
        filterPeopleByDepartment: user?.departmentId ? String(user.departmentId) : null
      };
    }
    return {};
  };

  // Filter data state for dropdowns
  const [businessUnitsData, setBusinessUnitsData] = useState([]);
  const [salesUsersData, setSalesUsersData] = useState([]);
  // HRIS user lookup: userId → { deptId, status }
  const [projectsRptUserLookup, setProjectsRptUserLookup] = useState(new Map());
  useEffect(() => {
    api.get('/md/internal-users/option-internal-user')
      .then(res => {
        const map = new Map();
        (res.data || []).forEach(u => {
          const deptId = u.departmentId || u.departementId;
          if (u.id != null) map.set(String(u.id), { deptId: deptId != null ? String(deptId) : null, status: u.status });
        });
        setProjectsRptUserLookup(map);
      })
      .catch(() => {});
  }, []);
  const [createdByUsersData, setCreatedByUsersData] = useState([]);
  const [branchesData, setBranchesData] = useState([]);
  const [taggingOptionsData, setTaggingOptionsData] = useState([]);
  const [sectorsData, setSectorsData] = useState([]);
  const [standardsData, setStandardsData] = useState([]);
  const [sourcesData, setSourcesData] = useState([]);
  const [servicesData, setServicesData] = useState([]);

  // Initialize filters from URL query parameters (runs first)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasUrlParams = Array.from(searchParams.keys()).length > 0;

    // parseParam returns [] for missing/all — use parseParamWithAlias when a fallback key is needed
    const parseParam = (key) => {
      const v = searchParams.get(key);
      if (!v || v === 'all') return [];
      return v.split(',').map((s) => s.trim()).filter(Boolean);
    };
    // Try key1 first; fall back to key2 if key1 yields nothing
    const parseParamWithAlias = (key1, key2) => {
      const v1 = parseParam(key1);
      return v1.length > 0 ? v1 : parseParam(key2);
    };

    if (hasUrlParams) {
      // Helper: resolve a dashboard period-key value to an explicit { from, to } date range.
      const resolvePeriodToRange = (periodVal, startKey, endKey) => {
        if (!periodVal || periodVal === 'all') return { from: '', to: '' };
        if (periodVal === 'custom') {
          return {
            from: searchParams.get(startKey) || '',
            to: searchParams.get(endKey) || '',
          };
        }
        const { startDate, endDate } = getPeriodDateRange(periodVal);
        return { from: startDate, to: endDate };
      };

      // Main period: Dashboard sends 'period' or 'createdAt'; legacy sent 'createdDate'
      const mainPeriod = searchParams.get('period') || searchParams.get('createdAt') || searchParams.get('createdDate') || '';
      const mainStart = searchParams.get('createdAtStartDate') || searchParams.get('customStartDate') || searchParams.get('startDate') || '';
      const mainEnd = searchParams.get('createdAtEndDate') || searchParams.get('customEndDate') || searchParams.get('endDate') || '';

      // projectStartDate-keyed navigation (periodKey: 'projectStartDate')
      const projectStartDateKey = searchParams.get('projectStartDate') || '';
      const projectStartRange = resolvePeriodToRange(projectStartDateKey, 'projectStartDateFrom', 'projectStartDateTo');

      // auditDate-keyed navigation (periodKey: 'auditDate') → filters by per-standard propose_date
      const auditDateKey = searchParams.get('auditDate') || '';
      const auditDateRange = resolvePeriodToRange(auditDateKey, 'proposeDateFrom', 'proposeDateTo');

      const urlFilters = {
        period: mainPeriod === 'all' ? '' : mainPeriod,
        startDate: mainPeriod === 'custom' ? mainStart : '',
        endDate: mainPeriod === 'custom' ? mainEnd : '',
        // Accept both report-native names and dashboard alias names
        assignedUser: parseParamWithAlias('assignedUser', 'people'),
        createdBy: parseParam('createdBy'),
        department: parseParamWithAlias('department', 'businessUnits'),
        branchId: parseParam('branchId'),
        sector: parseParam('sector'),
        standards: parseParamWithAlias('standards', 'standard'),
        source: parseParam('source'),
        service: parseParam('service'),
        taggingIds: parseParam('taggingIds'),
        createdAtFrom: searchParams.get('createdAtFrom') || '',
        createdAtTo: searchParams.get('createdAtTo') || '',
        // Resolve projectStartDate period key OR fall back to explicit date params
        projectStartDateFrom: projectStartRange.from || searchParams.get('projectStartDateFrom') || '',
        projectStartDateTo: projectStartRange.to || searchParams.get('projectStartDateTo') || '',
        projectEndDateFrom: searchParams.get('projectEndDateFrom') || '',
        projectEndDateTo: searchParams.get('projectEndDateTo') || '',
        // Resolve auditDate period key OR fall back to explicit propose_date params
        proposeDateFrom: auditDateRange.from || searchParams.get('proposeDateFrom') || '',
        proposeDateTo: auditDateRange.to || searchParams.get('proposeDateTo') || '',
      };

      setFilterData(urlFilters);
      setAppliedFilters(urlFilters);
      setFiltersInitializedFromUrl(true);
    } else {
      setFiltersInitializedFromUrl(false);
      // No URL params — restore from sessionStorage if available
      const saved = loadPersistedState();
      if (saved) {
        const restoredFilters = saved.filters ?? getEmptyProjectsFilters();
        const restoredSearch = saved.search ?? '';
        const restoredPage = saved.page ?? 0;
        const restoredSize = saved.size ?? DEFAULT_PAGE_SIZE;
        setFilterData(restoredFilters);
        setAppliedFilters(restoredFilters);
        setSearchQuery(restoredSearch);
        setPage(restoredPage);
        setSize(restoredSize);
      }
    }

    setUrlParamsProcessed(true);
  }, [location.search]);

  // Initialize department filter for view_department users (only if not from URL)
  useEffect(() => {
    if (!capabilitiesLoading && !filtersInitializedFromUrl && urlParamsProcessed) {
      if (canProject('view_department') && !canProject('view_global') && user?.departmentId) {
        const deptVal = [String(user.departmentId)];
        setFilterData(prev => ({ ...prev, department: deptVal }));
        setAppliedFilters(prev => ({ ...prev, department: deptVal }));
      }
    }
  }, [capabilitiesLoading, user?.departmentId, filtersInitializedFromUrl, urlParamsProcessed]);

  // When appliedFilters change, reset to first page
  useEffect(() => {
    setPage(0);
  }, [appliedFilters]);

  // Reload data when capabilities/URL are ready, or when page/size/sort/search or appliedFilters change
  useEffect(() => {
    if (!capabilitiesLoading && urlParamsProcessed) {
      loadProjects(page, size, sortConfig);
    }
  }, [capabilitiesLoading, urlParamsProcessed, page, size, sortConfig, searchQuery, appliedFilters]);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'JPY': return '¥';
      case 'SGD': return 'S$';
      case 'IND': return 'RUP';
      default: return 'Rp';
    }
  };

  const formatAmount = (amount, currencyCode = 'IDR') => {
    if (!amount || amount === 0) {
      return `${getCurrencySymbol(currencyCode)} 0`;
    }
    const symbol = getCurrencySymbol(currencyCode);
    const formattedAmount = amount.toLocaleString('id-ID');
    return `${symbol} ${formattedAmount}`;
  };

  const loadProjects = async (pageNum = page, pageSize = size, sortConfigOverride = null) => {
    setIsLoading(true);
    try {
      const params = { page: pageNum, size: pageSize };

      // Server-side sort
      const effectiveSort = sortConfigOverride ?? sortConfig;
      if (effectiveSort?.key && PROJECTS_REPORT_SORT_KEY_TO_BACKEND[effectiveSort.key]) {
        const backendSortProperty = PROJECTS_REPORT_SORT_KEY_TO_BACKEND[effectiveSort.key];
        params.sort = `${backendSortProperty},${effectiveSort.direction}`;
      }
      if (searchQuery && searchQuery.trim()) params.search = searchQuery.trim();

      // Permission-based filtering hierarchy
      if (!canProject('view_global')) {
        if (canProject('view_department')) {
          const assignedUserEmpty = !appliedFilters.assignedUser?.length;
          const departmentEmpty = !appliedFilters.department?.length;
          if (assignedUserEmpty && departmentEmpty && user?.departmentId) {
            params.viewDepartmentId = user.departmentId;
          }
        } else if (canProject('view_own')) {
          if (!appliedFilters.assignedUser?.length && user?.id) {
            params.viewOwnUserId = user.id;
          }
        }
      }

      // Multiselect filters (comma-separated for backend)
      const assignedUserParam = toFilterParam(appliedFilters.assignedUser);
      const createdByParam = toFilterParam(appliedFilters.createdBy);
      const departmentParam = toFilterParam(appliedFilters.department);
      const sectorParam = toFilterParam(appliedFilters.sector);
      const standardsParam = toFilterParam(appliedFilters.standards);
      const sourceParam = toFilterParam(appliedFilters.source);
      const serviceParam = toFilterParam(appliedFilters.service);
      const taggingParam = toFilterParam(appliedFilters.taggingIds);
      const branchParam = toFilterParam(appliedFilters.branchId);

      if (assignedUserParam !== 'all') params.assignedTo = assignedUserParam;
      if (createdByParam !== 'all') params.createdById = createdByParam;
      if (departmentParam !== 'all') params.department = departmentParam;
      if (branchParam !== 'all') params.branchId = branchParam;
      if (sectorParam !== 'all') params.sector = sectorParam;
      if (sourceParam !== 'all') params.source = sourceParam;
      if (standardsParam !== 'all') params.standards = standardsParam;
      if (serviceParam !== 'all') params.service = serviceParam;
      if (taggingParam !== 'all') params.tagging = taggingParam;

      // Period filter → createdAt date range (period picker)
      const { startDate, endDate } = getActivePeriodRange(appliedFilters);
      if (startDate) params.createdAtFrom = startDate;
      if (endDate) params.createdAtTo = endDate;

      // Explicit createdAt date range (date picker fields — overrides period if set)
      if (appliedFilters.createdAtFrom) params.createdAtFrom = appliedFilters.createdAtFrom;
      if (appliedFilters.createdAtTo) params.createdAtTo = appliedFilters.createdAtTo;

      // Project start date range (from dateRange fields)
      if (appliedFilters.projectStartDateFrom) params.projectStartDateFrom = appliedFilters.projectStartDateFrom;
      if (appliedFilters.projectStartDateTo) params.projectStartDateTo = appliedFilters.projectStartDateTo;

      // Project end date range (from dateRange fields)
      if (appliedFilters.projectEndDateFrom) params.projectEndDateFrom = appliedFilters.projectEndDateFrom;
      if (appliedFilters.projectEndDateTo) params.projectEndDateTo = appliedFilters.projectEndDateTo;

      // Audit date range → per-standard propose_date (Sales Funnel "Project" navigation)
      if (appliedFilters.proposeDateFrom) params.proposeDateFrom = appliedFilters.proposeDateFrom;
      if (appliedFilters.proposeDateTo) params.proposeDateTo = appliedFilters.proposeDateTo;

      const response = await api.get('/crm/projects', { params });
      const data = response.data;
      let list = Array.isArray(data) ? data : (data?.content ?? data?.data ?? []);
      const currencyCodeCache = new Map();
      const mapProject = async (item) => {
        let currencyCode = String(item.displayCurrencyCode || '').trim().toUpperCase();
        if (!currencyCode && item.displayCurrencyId) {
          const currencyId = String(item.displayCurrencyId);
          if (!currencyCodeCache.has(currencyId)) {
            currencyCodeCache.set(currencyId,
              api.get(`/crm/currencies/${item.displayCurrencyId}`)
                .then(res => String(res.data?.currencyCode || '').trim().toUpperCase())
                .catch(() => '')
            );
          }
          currencyCode = await currencyCodeCache.get(currencyId);
        }
        currencyCode = currencyCode || 'IDR';
        const amountItem = {
          ...item,
          displayCurrencyCode: currencyCode,
          ...(currencyCode === 'IDR' ? { exchangeRateAtCreation: 1 } : {})
        };
        return {
          ...amountItem,
          project: item.projectCode,
          client: item.companyName,
          amount: getProjectAmountInIdr(amountItem),
          grandTotal: item.grandTotal,
          departmentName: item.departmentName,
          departmentId: item.departmentId,
          assignedTo: item.assignedTo,
          team: item.team,
          proposeDates: item.proposeDates || []
        };
      };

      const isPagedResponse = data && typeof data.totalElements === 'number';
      if (isPagedResponse) {
        setTotalElements(data.totalElements);
        setTotalPages(data.totalPages ?? Math.max(1, Math.ceil(data.totalElements / pageSize)));
        const mappedData = await Promise.all(list.map(mapProject));
        setProjectsData(mappedData);
        return;
      }

      setTotalElements(list.length);
      setTotalPages(1);
      const mappedData = await Promise.all(list.map(mapProject));
      setProjectsData(mappedData);
    } catch (error) {
      console.error('Error loading projects:', error);
      showAlert('danger', 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch business units data from backend
  const fetchBusinessUnitsData = async () => {
    try {
      const response = await api.get('/md/departement');
      setBusinessUnitsData((response.data || []).map(d => ({ id: d.id, name: d.name })));
    } catch (error) {
      console.error('Error fetching business units data:', error);
      setBusinessUnitsData([]);
    }
  };

  // Fetch sales users data from backend
  const fetchSalesUsersData = async () => {
    try {
      const response = await api.get('/dashboard/sales-users');
      if (response.data && response.data.salesUsers) {
        let users = response.data.salesUsers;
        const overrides = getFilterOverrides();
        if (overrides.filterPeopleByDepartment) {
          users = users.filter(u => {
            const deptId = u.departmentId;
            return deptId !== null && deptId !== undefined &&
              String(deptId) === String(overrides.filterPeopleByDepartment);
          });
        }
        setSalesUsersData(users);
      }
    } catch (error) {
      console.error('Error fetching sales users data:', error);
      setSalesUsersData([]);
    }
  };

  // Fetch sectors data from backend
  const fetchSectorsData = async () => {
    try {
      const response = await api.get('/dashboard/sectors');
      if (response.data && response.data.sectors) {
        setSectorsData(response.data.sectors);
      }
    } catch (error) {
      console.error('Error fetching sectors data:', error);
      setSectorsData([]);
    }
  };

  // Fetch sources data from backend
  const fetchSourcesData = async () => {
    try {
      const response = await api.get('/dashboard/sources');
      if (response.data && response.data.sources) {
        setSourcesData(response.data.sources);
      }
    } catch (error) {
      console.error('Error fetching sources data:', error);
      setSourcesData([]);
    }
  };

  const fetchStandardsData = async () => {
    try {
      const response = await api.get('/dashboard/standards');
      if (response.data && response.data.standards) {
        setStandardsData(response.data.standards);
      }
    } catch (error) {
      console.error('Error fetching standards data:', error);
      setStandardsData([]);
    }
  };

  const fetchServicesData = async () => {
    try {
      const response = await api.get('/crm/services/options');
      setServicesData((response.data || []).map(s => ({ id: s.id ?? s.value, name: s.name ?? s.label })));
    } catch (error) {
      console.error('Error fetching services data:', error);
      setServicesData([]);
    }
  };

  const fetchCreatedByUsersData = async () => {
    try {
      const response = await api.get('/md/internal-users/option-internal-user');
      let users = response.data || [];
      const overrides = getFilterOverrides();
      if (overrides.filterPeopleByDepartment) {
        users = users.filter(u => {
          const deptId = u.departmentId || u.departementId;
          return deptId != null && String(deptId) === String(overrides.filterPeopleByDepartment);
        });
      }
      setCreatedByUsersData(users.map(u => ({ id: u.id, name: u.fullName || `${u.firstName} ${u.lastName}`.trim() })));
    } catch (error) {
      console.error('Error fetching created by users data:', error);
      setCreatedByUsersData([]);
    }
  };

  const fetchBranchesData = async () => {
    try {
      if (!user?.id) return;
      const response = await api.get(`/user-branches/user/${user.id}`);
      const branchData = response.data?.data ?? response.data ?? [];
      setBranchesData(Array.isArray(branchData) ? branchData : []);
    } catch (error) {
      console.error('Error fetching branches data:', error);
      setBranchesData([]);
    }
  };

  const fetchTaggingOptionsData = async () => {
    try {
      const response = await api.get('/md/tagging', { params: { moduleKey: 'companies' } });
      setTaggingOptionsData(response.data || []);
    } catch (error) {
      console.error('Error fetching tagging options data:', error);
      setTaggingOptionsData([]);
    }
  };

  // Load filter options on mount
  useEffect(() => {
    const enabledFilters = getEnabledFilters();
    if (enabledFilters.includes('department')) fetchBusinessUnitsData();
    if (enabledFilters.includes('assignedUser')) fetchSalesUsersData();
    if (enabledFilters.includes('createdBy')) fetchCreatedByUsersData();
    if (enabledFilters.includes('branchId')) fetchBranchesData();
    if (enabledFilters.includes('sector')) fetchSectorsData();
    if (enabledFilters.includes('standards')) fetchStandardsData();
    if (enabledFilters.includes('source')) fetchSourcesData();
    if (enabledFilters.includes('service')) fetchServicesData();
    if (enabledFilters.includes('taggingIds')) fetchTaggingOptionsData();
    // Always fetch sector/source/standards as they're in default fallback
    if (!enabledFilters.includes('sector')) fetchSectorsData();
    if (!enabledFilters.includes('source')) fetchSourcesData();
    if (!enabledFilters.includes('standards')) fetchStandardsData();
    if (!enabledFilters.includes('service')) fetchServicesData();
    if (!enabledFilters.includes('taggingIds')) fetchTaggingOptionsData();
  }, [user?.departmentId]);

  // Cascade: filter assignedUser (salesUsersData) by selected department(s)
  const cascadedProjectsAssignedUser = useMemo(() => {
    const selectedDepts = (filterData.department || []).map(String);
    const base = salesUsersData.map(u => {
      const entry = projectsRptUserLookup.get(String(u.id));
      const name = u.name || u.fullName || '';
      return { value: String(u.id), label: entry?.status === '0' ? `${name} (Inactive)` : name };
    });
    if (!selectedDepts.length) return base;
    return base.filter(u => {
      const entry = projectsRptUserLookup.get(u.value);
      return entry?.deptId != null && selectedDepts.includes(entry.deptId);
    });
  }, [filterData.department, salesUsersData, projectsRptUserLookup]);

  // createdBy shows all users regardless of selected department
  const cascadedProjectsCreatedBy = useMemo(() => {
    return createdByUsersData.map(u => {
      const entry = projectsRptUserLookup.get(String(u.id));
      return { value: String(u.id), label: entry?.status === '0' ? `${u.name} (Inactive)` : (u.name || '') };
    });
  }, [createdByUsersData, projectsRptUserLookup]);

  // Build filterFields config for AdvancedFilterPanel
  const filterFields = useMemo(() => {
    const enabledFilters = getEnabledFilters();
    const fields = [];

    fields.push({
      type: 'dateRange',
      label: 'Created At',
      fromKey: 'createdAtFrom',
      toKey: 'createdAtTo',
    });

    // Project date ranges as dateRange fields
    fields.push({
      type: 'dateRange',
      label: 'Project Start Date',
      fromKey: 'projectStartDateFrom',
      toKey: 'projectStartDateTo',
    });

    fields.push({
      type: 'dateRange',
      label: 'Project End Date',
      fromKey: 'projectEndDateFrom',
      toKey: 'projectEndDateTo',
    });

    fields.push({
      type: 'dateRange',
      label: 'Audit Date',
      fromKey: 'proposeDateFrom',
      toKey: 'proposeDateTo',
    });

    if (enabledFilters.includes('department')) {
      fields.push({
        key: 'department',
        label: 'Department',
        type: 'multiselect',
        options: businessUnitsData.map(d => ({ value: String(d.id), label: d.name || '' })),
        disabled: getFilterOverrides().isDepartmentLocked,
      });
    }

    if (enabledFilters.includes('assignedUser')) {
      fields.push({
        key: 'assignedUser',
        label: 'Assigned To',
        type: 'multiselect',
        options: cascadedProjectsAssignedUser,
      });
    }

    if (enabledFilters.includes('createdBy')) {
      fields.push({
        key: 'createdBy',
        label: 'Created By',
        type: 'multiselect',
        options: cascadedProjectsCreatedBy,
      });
    }

    if (enabledFilters.includes('branchId')) {
      fields.push({
        key: 'branchId',
        label: 'Branch',
        type: 'multiselect',
        options: branchesData.map(b => ({ value: String(b.id), label: b.name || String(b.id) })),
      });
    }

    fields.push({
      key: 'sector',
      label: 'Sector',
      type: 'multiselect',
      options: sectorsData.map(s => ({ value: String(s.id), label: s.name || '' })),
    });

    fields.push({
      key: 'standards',
      label: 'Standards',
      type: 'multiselect',
      options: standardsData.map(s => ({ value: String(s.id), label: s.name || '' })),
    });

    fields.push({
      key: 'source',
      label: 'Source',
      type: 'multiselect',
      options: sourcesData.map(s => ({ value: String(s.id), label: s.name || '' })),
    });

    fields.push({
      key: 'service',
      label: 'Service',
      type: 'multiselect',
      options: servicesData.map(s => ({ value: String(s.id), label: s.name || '' })),
    });

    fields.push({
      key: 'taggingIds',
      label: 'Tagging',
      type: 'multiselect',
      options: taggingOptionsData.map(t => ({ value: String(t.id), label: t.name || String(t.id) })),
    });

    return fields;
  }, [
    salesUsersData, createdByUsersData, businessUnitsData, branchesData,
    sectorsData, standardsData, sourcesData, servicesData, taggingOptionsData,
    cascadedProjectsAssignedUser, cascadedProjectsCreatedBy, projectsRptUserLookup,
    capabilitiesLoading, user?.departmentId,
  ]);

  // Active filter count (drives badge on filter button)
  const activeFilterCount = getActiveFilterCount(appliedFilters, filterFields);

  // Filter tags for FilterAwarenessBar
  const filterTags = useMemo(() => {
    const tags = [];

    // Period tag
    if (appliedFilters.period) {
      tags.push({
        key: 'period',
        label: `Period: ${String(appliedFilters.period).replace(/_/g, ' ')}`,
      });
    } else if (appliedFilters.startDate || appliedFilters.endDate) {
      tags.push({
        key: 'period',
        label: `Period: ${[appliedFilters.startDate, appliedFilters.endDate].filter(Boolean).join(' - ')}`,
      });
    }

    // Multiselect / select fields
    filterFields.forEach((field) => {
      if (field.type === 'dateRange') {
        const from = appliedFilters[field.fromKey];
        const to = appliedFilters[field.toKey];
        if (from || to) {
          tags.push({
            key: field.fromKey,
            label: `${field.label}: ${[from, to].filter(Boolean).join(' - ')}`,
          });
        }
        return;
      }
      const value = appliedFilters[field.key];
      const isEmpty = value === '' || value === null || value === undefined ||
        (Array.isArray(value) && value.length === 0);
      if (isEmpty) return;

      if (Array.isArray(value)) {
        tags.push({ key: field.key, label: `${field.label}: ${value.length} selected` });
        return;
      }

      const option = (field.options || []).find(opt => String(opt.value) === String(value));
      tags.push({ key: field.key, label: `${field.label}: ${option ? option.label : value}` });
    });

    return tags;
  }, [appliedFilters, filterFields]);

  // getClearedFilters — clears one filter key from a filter object
  const getClearedFilters = (filters, key) => {
    const periodKeys = ['period', 'startDate', 'endDate'];
    const keysToClear = periodKeys.includes(key) ? periodKeys : [key];

    // Also handle dateRange fromKey/toKey
    const matchingDateRange = filterFields.find(
      f => f.type === 'dateRange' && (f.fromKey === key || f.toKey === key)
    );
    if (matchingDateRange) {
      return {
        ...filters,
        [matchingDateRange.fromKey]: '',
        [matchingDateRange.toKey]: '',
      };
    }

    const nextFilters = { ...filters };
    keysToClear.forEach((fieldKey) => {
      const fieldConfig = filterFields.find(f => f.key === fieldKey);
      const isMulti = fieldConfig?.type === 'multiselect';
      nextFilters[fieldKey] = isMulti ? [] : '';
    });
    return nextFilters;
  };

  // Handler: live filter edits (inside offcanvas, not yet applied)
  const handleFilterChange = (name, value) => {
    // Prevent changing locked fields
    const overrides = getFilterOverrides();
    if (name === 'department' && overrides.isDepartmentLocked) return;

    setFilterData(prev => ({ ...prev, [name]: value }));
  };

  // Handler: Apply — commit filterData → appliedFilters and fetch
  const handleFilterApply = () => {
    const { startDate, endDate } = getActivePeriodRange(filterData);
    const applied = { ...filterData, startDate, endDate };
    setAppliedFilters(applied);
    persistState({ filters: applied, search: searchQuery, page: 0, size });
    // page reset handled by useEffect watching appliedFilters
  };

  // Handler: Reset — clear both filterData and appliedFilters
  const handleFilterReset = () => {
    const overrides = getFilterOverrides();
    const empty = {
      ...getEmptyProjectsFilters(),
      department: overrides.department ?? [],
    };
    setFilterData(empty);
    setAppliedFilters(empty);
    persistState({ filters: empty, search: '', page: 0, size });
  };

  // Handler: chip removal from FilterAwarenessBar
  const handleChipRemove = (key) => {
    const periodKeys = ['period', 'startDate', 'endDate'];
    const keysToCheck = periodKeys.includes(key) ? periodKeys : [key];
    const hasActiveValue = keysToCheck.some((fieldKey) => {
      const value = appliedFilters[fieldKey];
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    });
    if (!hasActiveValue) {
      // Also check dateRange fields
      const matchingDateRange = filterFields.find(
        f => f.type === 'dateRange' && (f.fromKey === key || f.toKey === key)
      );
      if (!matchingDateRange) return;
    }

    const nextAppliedFilters = getClearedFilters(appliedFilters, key);
    setFilterData(prev => getClearedFilters(prev, key));
    setAppliedFilters(nextAppliedFilters);
    persistState({ filters: nextAppliedFilters, search: searchQuery, page: 0, size });
  };

  const handleExportExcel = () => {
    if (!canProject('export_data')) {
      showAlert('danger', 'You do not have permission to export data.');
      return;
    }
    try {
      const dataToExport = searchQuery
        ? projectsData.filter(item =>
            item.project?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.client?.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : projectsData;

      const excelData = dataToExport.map(row => {
        const auditDates = (row.proposeDates || [])
          .map(d => {
            const parts = d.split('-');
            if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
            return d;
          })
          .join(', ') || '-';
        return {
          'Project': row.project || '',
          'Client': row.client || '',
          'Assigned To': row.assignedTo || '-',
          'Department': row.departmentName || 'N/A',
          'Standard': Array.isArray(row.standards) && row.standards.length > 0
            ? row.standards.map(s => s?.name || s?.standardName || s).filter(Boolean).join(', ')
            : (row.standardName || row.standard || '-'),
          'Project Start Date': row.projectDate ? formatDateDDMMYYYY(row.projectDate) : '',
          'Project End Date': row.validUntil ? formatDateDDMMYYYY(row.validUntil) : '',
          'Partner Type': row.partnerType || '',
          // Amount is already converted to the IDR base currency during mapping.
          'Amount': formatAmount(row.amount, 'IDR'),
          'Audit Date': auditDates,
          'Team': row.team || 'N/A',
          'Created At': row.createdAt ? formatDateDDMMYYYY(row.createdAt) : '',
          'Status': row.statusDesc || 'Draft'
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const columnWidths = [
        { wch: 20 }, { wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
        { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 20 }, { wch: 25 },
        { wch: 20 }, { wch: 15 }, { wch: 15 }
      ];
      worksheet['!cols'] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Projects Report');

      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      XLSX.writeFile(workbook, `projects_report_${timestamp}.xlsx`);

      showAlert('success', 'Projects report exported to Excel successfully!');
    } catch (e) {
      console.error('Export Excel failed:', e);
      showAlert('danger', 'Failed to export projects report to Excel. Please try again.');
    }
  };

  const columns = [
    {
      key: 'project',
      label: 'Project',
      className: 'text-truncate',
      render: (value, row) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/projects/detail/${row.leadId}`);
          }}
          className="text-primary text-decoration-none text-truncate d-inline-block"
          style={{ cursor: 'pointer', maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value}
        </a>
      )
    },
    {
      key: 'client',
      label: 'Client',
      className: 'text-truncate',
      render: (value) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: '200px', width: '200px' }} title={value}>
          {value}
        </span>
      )
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      className: 'text-truncate',
      render: (value) => {
        const assignedName = value || '-';
        return (
          <span className="text-truncate d-inline-block" style={{ maxWidth: '150px', width: '150px' }} title={assignedName}>
            {assignedName}
          </span>
        );
      }
    },
    {
      key: 'departmentName',
      label: 'Department',
      className: 'text-truncate',
      render: (value) => {
        const deptName = value || 'N/A';
        return (
          <span className="text-truncate d-inline-block" style={{ maxWidth: '150px', width: '150px' }} title={deptName}>
            {deptName}
          </span>
        );
      }
    },
    {
      key: 'standardName',
      label: 'Standard',
      className: 'text-truncate',
      render: (value, item) => {
        let standardValue;
        if (Array.isArray(item.standards) && item.standards.length > 0) {
          standardValue = item.standards.map(s => s?.name || s?.standardName || s).filter(Boolean).join(', ');
        } else {
          standardValue = value || item.standardName || item.standard || '-';
        }
        return (
          <span className="text-truncate d-inline-block" style={{ maxWidth: '150px', width: '150px' }} title={standardValue}>
            {standardValue}
          </span>
        );
      }
    },
    {
      key: 'projectDate',
      label: 'Project Start Date',
      type: 'date',
      className: 'text-truncate',
      render: (value) => {
        const dateValue = value ? formatDateDDMMYYYY(value) : '';
        return (
          <span className="text-truncate d-inline-block" style={{ maxWidth: '130px', width: '130px' }} title={dateValue}>
            {dateValue}
          </span>
        );
      }
    },
    {
      key: 'validUntil',
      label: 'Project End Date',
      type: 'date',
      className: 'text-truncate',
      render: (value) => {
        const dateValue = value ? formatDateDDMMYYYY(value) : '';
        return (
          <span className="text-truncate d-inline-block" style={{ maxWidth: '130px', width: '130px' }} title={dateValue}>
            {dateValue}
          </span>
        );
      }
    },
    {
      key: 'partnerType',
      label: 'Partner Type',
      className: 'text-truncate',
      render: (value) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: '150px', width: '150px' }} title={value}>
          {value}
        </span>
      )
    },
    {
      key: 'amount',
      label: 'Amount',
      className: 'text-truncate',
      render: (value, item) => {
        // `value` is IDR-converted, matching the Projects list.
        const formattedValue = formatAmount(value, 'IDR');
        return (
          <span className="text-truncate d-inline-block" style={{ maxWidth: '150px', width: '150px' }} title={formattedValue}>
            {formattedValue}
          </span>
        );
      }
    },
    {
      key: 'auditDates',
      label: 'Audit Date',
      sortable: false,
      className: 'text-truncate',
      render: (value, item) => {
        const dates = item.proposeDates || [];
        if (dates.length === 0) return <span className="text-muted">-</span>;
        const formatted = dates
          .map(d => {
            const parts = d.split('-');
            if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
            return d;
          })
          .join(', ');
        return (
          <span className="text-truncate d-inline-block" style={{ maxWidth: '180px', width: '180px' }} title={formatted}>
            {formatted}
          </span>
        );
      }
    },
    {
      key: 'createdAt',
      label: 'Created Date',
      type: 'date',
      className: 'text-truncate',
      render: (value, row) => {
        const dateValue = value || row.created_at;
        const formattedDate = dateValue ? formatDateDDMMYYYY(dateValue) : '-';
        return (
          <span className="text-truncate d-inline-block" style={{ maxWidth: '130px', width: '130px' }} title={formattedDate}>
            {formattedDate}
          </span>
        );
      }
    }
  ];

  return (
    <div className="container-xl">
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col-md-3">
          <h3 className="page-title m-0">Projects Reports</h3>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      data={projectsData}
                      columns={columns}
                      isLoading={isLoading}
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          onSearchSubmit={(q) => { setSearchQuery(q); setPage(0); }}
                          placeholder="Search projects..."
                        />
                      }
                      filterComponent={
                        <FilterButton offcanvasId="offcanvasProjectsReport" activeCount={activeFilterCount} />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={canProject('export_data') ? handleExportExcel : undefined}
                        />
                      }
                      belowTopControlsContent={
                        <FilterAwarenessBar
                          title="Projects Report is showing a filtered view"
                          summary={`Filtered by ${activeFilterCount} ${activeFilterCount === 1 ? 'criterion' : 'criteria'}`}
                          detail={filterTags.map((tag) => tag.label).join(' · ')}
                          loading={isLoading}
                          activeCount={activeFilterCount}
                          filterTags={filterTags}
                          onRemoveFilter={handleChipRemove}
                          onResetFilters={handleFilterReset}
                        />
                      }
                      pagination={{
                        currentPage: page + 1,
                        totalRows: totalElements,
                        rowsPerPage: size,
                        onPageChange: (newPage) => {
                          const nextPage = newPage - 1;
                          setPage(nextPage);
                          persistState({ filters: appliedFilters, search: searchQuery, page: nextPage, size });
                          loadProjects(nextPage, size, sortConfig);
                        },
                        onRowsPerPageChange: (newSize) => {
                          setSize(newSize);
                          setPage(0);
                          persistState({ filters: appliedFilters, search: searchQuery, page: 0, size: newSize });
                          loadProjects(0, newSize, sortConfig);
                        },
                      }}
                      onSortChange={(newSortConfig) => {
                        setSortConfig(newSortConfig);
                        setPage(0);
                        loadProjects(0, size, newSortConfig);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Unified Filter Panel */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasProjectsReport"
        title="Filter Projects"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={filterFields}
      />
    </div>
  );
};

export default ProjectsReports;
